import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SimulationResponse } from '@/types/simulation.ts';
import { useCatalog } from '@/hooks/useCatalog.ts';

export type SortByKey = 'default' | 'name' | 'difficultyLevel' | 'priceRange' | 'createdAt';
export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  key: SortByKey;
  direction: SortDirection;
}

export function useSortedSimulations(
  simulations: SimulationResponse[],
  sort: SortConfig,
): SimulationResponse[] {
  const { i18n } = useTranslation();
  const { data: priceRangeEntries } = useCatalog('PriceRange');

  const priceRangeIndex = useMemo(() => {
    const map = new Map<string, number>();
    [...(priceRangeEntries ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((entry, i) => map.set(entry.slug, i));
    return map;
  }, [priceRangeEntries]);

  return useMemo(() => {
    if (sort.key === 'default') return simulations;

    const dir = sort.direction === 'asc' ? 1 : -1;
    const sorted = [...simulations];

    sorted.sort((a, b) => {
      switch (sort.key) {
        case 'name':
          return dir * a.name.localeCompare(b.name, i18n.language);

        case 'difficultyLevel':
          return dir * (a.difficultyLevel - b.difficultyLevel);

        case 'priceRange': {
          const ai = a.priceRange ? (priceRangeIndex.get(a.priceRange) ?? Infinity) : Infinity;
          const bi = b.priceRange ? (priceRangeIndex.get(b.priceRange) ?? Infinity) : Infinity;
          return dir * (ai - bi);
        }

        case 'createdAt':
          return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

        default:
          return 0;
      }
    });

    return sorted;
  }, [simulations, sort.key, sort.direction, i18n.language, priceRangeIndex]);
}
